import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@ntconsult/aluma-react';
import { ConfirmDialog } from '@common/components/ConfirmDialog';
import { ShareModal } from '@features/shares';
import { useDeleteFolder } from './useDeleteFolder';
import { RenameFolderModal } from '../components/RenameFolderModal';
import { triggerBrowserDownload } from '@common/utils/download';
import { fileService } from '@features/files/services/fileService';
import type { Folder } from '../types';

/**
 * Centraliza ações sobre a pasta atual exibidas no header do FileExplorer
 * (compartilhar, copiar link, renomear, excluir).
 *
 * O consumidor renderiza `dialogs` em algum lugar do JSX e chama
 * `handlers.*` a partir do menu/botões.
 */
export function useFolderActions(folder: Folder | undefined) {
  const { t } = useTranslation(['folders', 'common']);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutate: deleteFolder, isPending: deletePending } = useDeleteFolder();

  const [shareOpen, setShareOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handlers = {
    share: () => setShareOpen(true),
    rename: () => setRenameOpen(true),
    requestDelete: () => setDeleteOpen(true),
    download: () => {
      if (!folder) return;
      triggerBrowserDownload(fileService.getFolderDownloadUrl(folder.id));
    },
    copyLink: () => {
      if (!folder) return;
      const url = `${window.location.origin}/files/${folder.id}`;
      navigator.clipboard.writeText(url).then(() => {
        toast.success({ title: t('folders:actions.linkCopied') });
      });
    },
  };

  const confirmDelete = () => {
    if (!folder) return;
    deleteFolder(folder.id, {
      onSuccess: () => {
        setDeleteOpen(false);
        navigate(folder.parentId ? `/files/${folder.parentId}` : '/files');
      },
      onError: () => setDeleteOpen(false),
    });
  };

  const dialogs = folder ? (
    <>
      {shareOpen && (
        <ShareModal
          isOpen
          onClose={() => setShareOpen(false)}
          resourceType="folder"
          resourceId={folder.id}
          resourceName={folder.name}
        />
      )}

      <RenameFolderModal
        isOpen={renameOpen}
        onClose={() => setRenameOpen(false)}
        folder={folder}
      />

      <ConfirmDialog
        open={deleteOpen}
        title={t('folders:deleteConfirm.title')}
        description={t('folders:deleteConfirm.description', { name: folder.name })}
        confirmLabel={t('common:confirmDelete.confirm')}
        pending={deletePending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  ) : null;

  return { handlers, dialogs };
}
